import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const EvaluationContext = createContext();

export const useEvaluation = () => {
  const context = useContext(EvaluationContext);
  if (!context) {
    throw new Error('useEvaluation must be used within an EvaluationProvider');
  }
  return context;
};

export const EvaluationProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Cache par courseId : { [courseId]: [evaluations enrichies] }
  const [evaluationsByCourse, setEvaluationsByCourse] = useState({});

  // Types d'évaluations (conservés pour compat ascendante)
  const evaluationTypes = {
    QUIZ: 'quiz',
    ASSIGNMENT: 'assignment',
    PROJECT: 'project',
    EXAM: 'exam',
  };

  // Statuts d'évaluation (conservés pour compat ascendante)
  const evaluationStatus = {
    NOT_STARTED: 'not_started',
    IN_PROGRESS: 'in_progress',
    SUBMITTED: 'submitted',
    GRADED: 'graded',
    PASSED: 'passed',
    FAILED: 'failed',
  };

  // ── Chargement des évaluations d'un cours ────────────────────────────────
  const loadCourseEvaluations = useCallback(async (courseId) => {
    if (!courseId) return { success: false, error: 'courseId manquant' };
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`/api/evaluations/course/${courseId}`);
      const evals = data.evaluations || [];
      setEvaluationsByCourse(prev => ({ ...prev, [courseId]: evals }));
      return { success: true, evaluations: evals };
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Récupérer une évaluation par ID (ou depuis cache) ─────────────────────
  const getEvaluationById = useCallback(async (evaluationId) => {
    // Check cache
    for (const courseId of Object.keys(evaluationsByCourse)) {
      const found = (evaluationsByCourse[courseId] || []).find(e => e.id === evaluationId);
      if (found) return { success: true, evaluation: found };
    }
    // Sinon fetch
    try {
      const { data } = await axios.get(`/api/evaluations/${evaluationId}`);
      return { success: true, evaluation: data.evaluation };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    }
  }, [evaluationsByCourse]);

  // ── Récupérer l'éval d'un module ──────────────────────────────────────────
  const getEvaluationByModule = useCallback(async (moduleId) => {
    try {
      const { data } = await axios.get(`/api/evaluations/module/${moduleId}`);
      return { success: true, evaluation: data.evaluation };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    }
  }, []);

  // ── Créer une évaluation (instructeur) ────────────────────────────────────
  const createEvaluation = useCallback(async (payload) => {
    setLoading(true);
    try {
      const { data } = await axios.post('/api/evaluations', payload);
      return { success: true, evaluation: data.evaluation };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Modifier une évaluation ───────────────────────────────────────────────
  const updateEvaluation = useCallback(async (evaluationId, updates) => {
    try {
      const { data } = await axios.put(`/api/evaluations/${evaluationId}`, updates);
      return { success: true, evaluation: data.evaluation };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    }
  }, []);

  // ── Soumettre une tentative ──────────────────────────────────────────────
  const submitAttempt = useCallback(async (evaluationId, answers, timeSpentSeconds = 0) => {
    try {
      const { data } = await axios.post(`/api/evaluations/${evaluationId}/attempts`, {
        answers,
        timeSpentSeconds,
      });
      return {
        success: true,
        attempt: data.attempt,
        passed: data.passed,
        certificate: data.certificate,
        certificateIssuedNow: data.certificateIssuedNow,
        certificateEligible: data.certificateEligible,
        certificateEligibleReason: data.certificateEligibleReason,
        certificatePaiementRequis: data.certificatePaiementRequis === true,
        certificateLienAchat: data.certificateLienAchat || null,
      };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    }
  }, []);

  // ── Récupérer mes tentatives sur une éval ────────────────────────────────
  const getMyAttempts = useCallback(async (evaluationId) => {
    try {
      const { data } = await axios.get(`/api/evaluations/${evaluationId}/attempts`);
      return { success: true, attempts: data.attempts || [] };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || err.message };
    }
  }, []);

  // ── Accesseurs synchrones (depuis cache) ─────────────────────────────────
  const getEvaluationsByCourse = useCallback((courseId) => {
    return evaluationsByCourse[courseId] || [];
  }, [evaluationsByCourse]);

  const value = {
    loading,
    error,
    evaluationTypes,
    evaluationStatus,
    // Chargement
    loadCourseEvaluations,
    // Accesseurs
    getEvaluationsByCourse,
    getEvaluationById,
    getEvaluationByModule,
    // Instructeur
    createEvaluation,
    updateEvaluation,
    // Étudiant
    submitAttempt,
    getMyAttempts,
  };

  return (
    <EvaluationContext.Provider value={value}>
      {children}
    </EvaluationContext.Provider>
  );
};
